import { useEffect } from 'react';
import * as CookieConsent from 'vanilla-cookieconsent';
import 'vanilla-cookieconsent/dist/cookieconsent.css';
import './CookieConsent.css';
import { useTranslation } from 'react-i18next';

const CookieConsentWrapper = () => {
  const { i18n } = useTranslation();

  useEffect(() => {
    const currentLang = ['en', 'ro', 'es', 'de'].includes(i18n.language) ? i18n.language : 'en';
    console.log('=== COOKIE CONSENT INITIALIZING ===');
    console.log('Language:', currentLang);
    console.log('CookieConsent object:', CookieConsent);

    // Function to load all analytics scripts
    const loadAnalyticsScripts = () => {
      console.log('=== LOADING ANALYTICS SCRIPTS ===');

      // Load Microsoft Clarity
      if (!window.clarity) {
        console.log('Loading Clarity...');
        (function (c, l, a, r, i, t, y) {
          c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments) };
          t = l.createElement(r); t.async = 1; t.src = "https://www.clarity.ms/tag/" + i;
          y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y);
        })(window, document, "clarity", "script", "pax30yz27c");
      }

      // Load Google Tag Manager
      if (!window.dataLayer) {
        console.log('Loading Google Tag Manager...');
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          'gtm.start': new Date().getTime(),
          event: 'gtm.js'
        });

        const gtmScript = document.createElement('script');
        gtmScript.async = true;
        gtmScript.src = 'https://www.googletagmanager.com/gtm.js?id=GTM-KPQXJWFG';
        document.head.appendChild(gtmScript);
      }

      // Load Google Analytics (gtag.js)
      if (!window.gtag) {
        console.log('Loading Google Analytics...');
        window.dataLayer = window.dataLayer || [];
        function gtag() { window.dataLayer.push(arguments); }
        window.gtag = gtag;
        gtag('js', new Date());
        gtag('config', 'G-XXXXXXXXXX'); // Replace with your GA4 measurement ID

        const gaScript = document.createElement('script');
        gaScript.async = true;
        gaScript.src = 'https://www.googletagmanager.com/gtag/js?id=G-XXXXXXXXXX'; // Replace with your GA4 measurement ID
        document.head.appendChild(gaScript);
      }

      console.log('=== ANALYTICS SCRIPTS LOADED ===');
    };

    try {
      CookieConsent.run({
        mode: 'opt-in',
        revision: 1,

        autoLanguage: false,
        currentLang: currentLang,

        onFirstConsent: ({ cookie }) => {
          console.log('=== FIRST CONSENT CALLBACK ===', cookie);
          if (cookie.categories.includes('analytics')) {
            loadAnalyticsScripts();
          }
        },

        onConsent: ({ cookie }) => {
          console.log('=== CONSENT CALLBACK ===', cookie);
          if (cookie.categories.includes('analytics')) {
            loadAnalyticsScripts();
          }
        },

        onChange: ({ cookie }) => {
          console.log('=== CHANGE CALLBACK ===', cookie);
          if (cookie.categories.includes('analytics')) {
            loadAnalyticsScripts();
          }
        },

        cookie: {
          name: 'prostay_cookie_consent',
          domain: window.location.hostname,
          path: '/',
          expiresAfterDays: 365,
        },

        guiOptions: {
          consentModal: {
            layout: 'box',
            position: 'bottom right',
            equalWeightButtons: true,
            flipButtons: false,
          },
          preferencesModal: {
            layout: 'box',
            position: 'middle center',
            equalWeightButtons: true,
            flipButtons: false,
          },
        },

        categories: {
          necessary: {
            readOnly: true,
            enabled: true,
          },
          analytics: {
            enabled: true,
            readOnly: false,
            autoClear: {
              cookies: [{ name: /^(_ga|_gid|_clck|_clsk|_fbp|_gcl)/ }],
            },
          },
        },

        language: {
          default: 'en',
          translations: {
            en: {
              consentModal: {
                title: 'Welcome to Prostay',
                description: 'We use cookies to enhance your experience. By continuing to browse, you agree to our use of cookies for analytics. <a href="/privacy-policy/">Privacy Policy</a>',
                acceptAllBtn: 'Accept All',
                acceptNecessaryBtn: 'Reject All',
                showPreferencesBtn: 'Manage Preferences',
              },
              preferencesModal: {
                title: 'Cookie Preferences',
                acceptAllBtn: 'Accept All',
                acceptNecessaryBtn: 'Reject All',
                savePreferencesBtn: 'Save Preferences',
                closeIconLabel: 'Close',
                sections: [
                  {
                    title: 'Cookie Usage',
                    description: 'We use cookies to ensure basic functionalities and enhance your experience. You can manage your preferences at any time. <a href="/privacy-policy/">Privacy Policy</a>',
                  },
                  {
                    title: 'Strictly Necessary Cookies',
                    description: 'These cookies are essential for the website to function properly.',
                    linkedCategory: 'necessary',
                  },
                  {
                    title: 'Analytics Cookies',
                    description: 'These cookies help us understand how you use our website. All data is anonymized.',
                    linkedCategory: 'analytics',
                  },
                ],
              },
            },
            ro: {
              consentModal: {
                title: 'Bine ați venit la Prostay',
                description: 'Folosim cookie-uri pentru a îmbunătăți experiența dvs. Continuând să navigați, sunteți de acord cu utilizarea cookie-urilor. <a href="/ro/privacy-policy/">Politica de Confidențialitate</a>',
                acceptAllBtn: 'Acceptă Toate',
                acceptNecessaryBtn: 'Respinge Toate',
                showPreferencesBtn: 'Gestionează Preferințele',
              },
              preferencesModal: {
                title: 'Preferințe Cookie',
                acceptAllBtn: 'Acceptă Toate',
                acceptNecessaryBtn: 'Respinge Toate',
                savePreferencesBtn: 'Salvează Preferințele',
                closeIconLabel: 'Închide',
                sections: [
                  {
                    title: 'Utilizarea Cookie-urilor',
                    description: 'Folosim cookie-uri pentru funcționalitățile de bază și îmbunătățirea experienței. <a href="/ro/privacy-policy/">Politica de Confidențialitate</a>',
                  },
                  {
                    title: 'Cookie-uri Strict Necesare',
                    description: 'Aceste cookie-uri sunt esențiale pentru funcționarea site-ului.',
                    linkedCategory: 'necessary',
                  },
                  {
                    title: 'Cookie-uri de Analiză',
                    description: 'Aceste cookie-uri ne ajută să înțelegem cum utilizați site-ul.',
                    linkedCategory: 'analytics',
                  },
                ],
              },
            },
            es: {
              consentModal: {
                title: 'Bienvenido a Prostay',
                description: 'Usamos cookies para mejorar su experiencia. Al continuar navegando, acepta nuestro uso de cookies. <a href="/es/privacy-policy/">Política de Privacidad</a>',
                acceptAllBtn: 'Aceptar Todas',
                acceptNecessaryBtn: 'Rechazar Todas',
                showPreferencesBtn: 'Gestionar Preferencias',
              },
              preferencesModal: {
                title: 'Preferencias de Cookies',
                acceptAllBtn: 'Aceptar Todas',
                acceptNecessaryBtn: 'Rechazar Todas',
                savePreferencesBtn: 'Guardar Preferencias',
                closeIconLabel: 'Cerrar',
                sections: [
                  {
                    title: 'Uso de Cookies',
                    description: 'Utilizamos cookies para funcionalidades básicas y mejorar su experiencia. <a href="/es/privacy-policy/">Política de Privacidad</a>',
                  },
                  {
                    title: 'Cookies Estrictamente Necesarias',
                    description: 'Estas cookies son esenciales para el funcionamiento del sitio.',
                    linkedCategory: 'necessary',
                  },
                  {
                    title: 'Cookies de Análisis',
                    description: 'Estas cookies nos ayudan a entender cómo utiliza nuestro sitio.',
                    linkedCategory: 'analytics',
                  },
                ],
              },
            },
            de: {
              consentModal: {
                title: 'Willkommen bei Prostay',
                description: 'Wir verwenden Cookies, um Ihr Erlebnis zu verbessern. Wenn Sie weiter surfen, stimmen Sie zu. <a href="/de/privacy-policy/">Datenschutz</a>',
                acceptAllBtn: 'Alle Akzeptieren',
                acceptNecessaryBtn: 'Alle Ablehnen',
                showPreferencesBtn: 'Einstellungen Verwalten',
              },
              preferencesModal: {
                title: 'Cookie-Einstellungen',
                acceptAllBtn: 'Alle Akzeptieren',
                acceptNecessaryBtn: 'Alle Ablehnen',
                savePreferencesBtn: 'Einstellungen Speichern',
                closeIconLabel: 'Schließen',
                sections: [
                  {
                    title: 'Cookie-Verwendung',
                    description: 'Wir verwenden Cookies für grundlegende Funktionen und Verbesserungen. <a href="/de/privacy-policy/">Datenschutz</a>',
                  },
                  {
                    title: 'Unbedingt Erforderliche Cookies',
                    description: 'Diese Cookies sind für die Funktion der Website unerlässlich.',
                    linkedCategory: 'necessary',
                  },
                  {
                    title: 'Analyse-Cookies',
                    description: 'Diese Cookies helfen uns zu verstehen, wie Sie unsere Website nutzen.',
                    linkedCategory: 'analytics',
                  },
                ],
              },
            },
          },
        },
      });

      console.log('=== COOKIE CONSENT RUN COMPLETED ===');

      // Make it available globally for debugging
      window.CookieConsent = CookieConsent;
      window.showCookiePreferences = () => {
        console.log('=== MANUALLY CALLING showPreferences() ===');
        CookieConsent.showPreferences();
      };

      // Debug: Check if buttons exist and manually attach handlers
      setTimeout(() => {
        console.log('=== CHECKING DOM ===');
        const ccMain = document.querySelector('#cc-main');
        console.log('Cookie consent container:', ccMain);

        if (ccMain) {
          const consentModal = ccMain.querySelector('.cm');
          const prefsButton = ccMain.querySelector('button[data-role="showPreferences"]');
          const allButtons = ccMain.querySelectorAll('.cm button');

          console.log('Consent modal:', consentModal);
          console.log('Preferences button:', prefsButton);
          console.log('All buttons in consent modal:', allButtons);

          allButtons.forEach((btn, index) => {
            console.log(`Button ${index}:`, btn.textContent, btn.className, btn);

            // Try to find the manage preferences button by text
            const btnText = btn.textContent.trim().toLowerCase();
            if (btnText.includes('manage') || btnText.includes('preferences') ||
              btnText.includes('settings') || btnText.includes('gestionar') ||
              btnText.includes('gestionează') || btnText.includes('einstellungen')) {
              console.log('=== FOUND PREFERENCES BUTTON ===', btn);

              // Manually attach click handler
              btn.addEventListener('click', (e) => {
                console.log('=== PREFERENCES BUTTON CLICKED ===');
                e.preventDefault();
                e.stopPropagation();

                console.log('Calling showPreferences()...');
                CookieConsent.showPreferences();

                // Check if modal is in DOM after calling showPreferences
                setTimeout(() => {
                  const pmWrapper = document.querySelector('#cc-main .pm-wrapper');
                  const pm = document.querySelector('#cc-main .pm');

                  console.log('After showPreferences:');
                  console.log('  pm-wrapper:', pmWrapper);
                  console.log('  pm:', pm);

                  if (pmWrapper) {
                    console.log('  pm-wrapper display:', window.getComputedStyle(pmWrapper).display);
                    console.log('  pm-wrapper visibility:', window.getComputedStyle(pmWrapper).visibility);
                    console.log('  pm-wrapper opacity:', window.getComputedStyle(pmWrapper).opacity);
                    console.log('  pm-wrapper classes:', pmWrapper.className);

                    // Add show class and force it visible
                    pmWrapper.classList.add('show');
                    pmWrapper.style.cssText = 'display: flex !important; visibility: visible !important; opacity: 1 !important; position: fixed !important; top: 0 !important; left: 0 !important; right: 0 !important; bottom: 0 !important; z-index: 99999999 !important; align-items: flex-start !important; justify-content: center !important; overflow-y: auto !important; padding-top: 40px !important;';
                  }

                  if (pm) {
                    console.log('  pm display:', window.getComputedStyle(pm).display);
                    console.log('  pm visibility:', window.getComputedStyle(pm).visibility);
                    console.log('  pm opacity:', window.getComputedStyle(pm).opacity);
                    console.log('  pm classes:', pm.className);

                    // Add show class and force it visible with proper centering
                    pm.classList.add('show');
                    pm.style.cssText = 'display: block !important; visibility: visible !important; opacity: 1 !important;  margin-top: 0 !important; align-self: flex-start !important;';
                  }

                  // Add event listeners only to action buttons (not section toggle buttons)
                  if (pm) {
                    // Select only the footer action buttons (Accept All, Reject All, Save Preferences)
                    const actionButtons = pm.querySelectorAll('.pm__footer button, .pm__btn, button[class*="pm__btn"]');
                    console.log('Found action buttons in preferences modal:', actionButtons.length);

                    actionButtons.forEach((btn) => {
                      // Don't add listener to section toggle buttons
                      if (btn.classList.contains('section__toggle-btn') || btn.closest('.pm__section-title')) {
                        console.log('Skipping section toggle button:', btn);
                        return;
                      }

                      btn.addEventListener('click', () => {
                        console.log('=== ACTION BUTTON CLICKED ===', btn.textContent);

                        // Hide the modal after a short delay to allow the library to process
                        setTimeout(() => {
                          if (pmWrapper) {
                            pmWrapper.style.display = 'none';
                            pmWrapper.classList.remove('show');
                          }
                          if (pm) {
                            pm.classList.remove('show');
                          }
                          console.log('=== MODAL HIDDEN ===');
                        }, 100);
                      });
                    });

                    // Also handle close icon
                    const closeIcon = pm.querySelector('.pm__close-btn');
                    if (closeIcon) {
                      closeIcon.addEventListener('click', () => {
                        console.log('=== CLOSE ICON CLICKED ===');
                        if (pmWrapper) {
                          pmWrapper.style.display = 'none';
                          pmWrapper.classList.remove('show');
                        }
                        if (pm) {
                          pm.classList.remove('show');
                        }
                      });
                    }
                  }

                  console.log('=== MODAL SHOULD BE VISIBLE NOW ===');
                }, 100);
              });

              // Make sure it's clickable
              btn.style.pointerEvents = 'auto';
              btn.style.cursor = 'pointer';
            }
          });
        }
      }, 1000);

      // Check if analytics is already accepted and load scripts
      setTimeout(() => {
        const userPreferences = CookieConsent.getUserPreferences();
        console.log('=== CHECKING USER PREFERENCES ON LOAD ===', userPreferences);

        if (userPreferences.acceptedCategories && userPreferences.acceptedCategories.includes('analytics')) {
          console.log('Analytics already accepted, loading scripts...');
          loadAnalyticsScripts();
        }
      }, 500);

      console.log('CookieConsent initialized successfully');
    } catch (error) {
      console.error('=== ERROR INITIALIZING COOKIE CONSENT ===', error);
    }
  }, [i18n.language]);

  return null;
};

export default CookieConsentWrapper;
